import React, { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getIsZendeskFixedButtonVisibleSelector, getIsZendeskInitedSelector } from './zendeskSelectors';
import { useZendeskScriptInit } from './hooks/useZendeskScriptInit';
import { ZendeskButton } from './components/ZendeskButton';
import { ZENDESK_BUTTON_TYPES } from './components/ZendeskButtonConstants';
import { ZendeskContext } from './zendeskContext';
import { useZendeskAuth } from './hooks/useZendeskAuth';
import useZendeskHandleOpen from './hooks/useZendeskHandleOpen';
import useZendeskLocale from './hooks/useZendeskLocale';
import { getAppFeatures } from "../../app/appSelectors";
import { FEATURES } from "../../app/appConstants";
import useZendeskSetConversationTags from './hooks/useZendeskSetConversationTags';
import { ZendeskGlobalStyled } from './ZendeskGlobalStyled';
import { getLocationPlatformSelector } from '../Location/locationSelectors';
import { LOCATION_PLATFORM } from '../Location/locationConstants';

export type IConversationField = IConversationFieldSource;

export interface IConversationFieldSource {
    id: '13141618835612';
    value: 'support' | 'verify';
}

export interface IZendeskContext {
    openWebWidget: (conversationFields?: IConversationField[]) => void;
    closeWebWidget: () => void;
    setOnWidgetCloseCallback: (callback: () => void) => void;
    isWidgetOpen: boolean;
}

interface IZendesk {
    children: JSX.Element | JSX.Element[];
}

export const Zendesk = memo(({ children }: IZendesk) => {
    const isFixedButtonVisible = useSelector(getIsZendeskFixedButtonVisibleSelector);
    const features = useSelector(getAppFeatures);
    const isWebWidgetInited = useSelector(getIsZendeskInitedSelector);
    const platform = useSelector(getLocationPlatformSelector);
    const isDesktop = platform === LOCATION_PLATFORM.DESKTOP;
    const { setOnWidgetCloseCallback } = useZendeskScriptInit();
    useZendeskAuth();
    useZendeskLocale();
    const { openWebWidget, closeWebWidget, isWidgetOpen } = useZendeskHandleOpen();
    useZendeskSetConversationTags();

    const openWebWidgetHandler = useCallback(() => {
        isWebWidgetInited && openWebWidget();
    }, [isWebWidgetInited, openWebWidget]);

    return <ZendeskContext.Provider value={{
        openWebWidget: openWebWidgetHandler,
        closeWebWidget,
        setOnWidgetCloseCallback,
        isWidgetOpen,
    }}>
        {isDesktop && <ZendeskGlobalStyled/>}
        {children}
        {isFixedButtonVisible && features[FEATURES.SUPPORT] && <ZendeskButton type={ZENDESK_BUTTON_TYPES.fixed}/>}
    </ZendeskContext.Provider>;
});

export default Zendesk;
